import { useEffect } from 'react';

import { useAppDispatch, useAppSelector } from 'dux/app/hooks';

import { Box, Text, theme } from '@prose-ui';
import { legacyTheme, styled } from '@prose-ui/legacy';

import { Image } from 'Components/Image';

import { productsCategories } from 'constants/products';

import { subscriptionPerksContent } from 'assets/content/membership/membershipModal';

import useResponsiveVariant from 'utils/useResponsiveVariant';

import { trackMembershipModalViewed } from 'dux/tracking/actions';
import { getMembershipPerksTitle, getUserSubscriptionDiscount } from 'dux/user/selectors';

import { BasicModal } from './BasicModal';
import { PerksContentTitle } from './PerksContentTitle';

const StyledModal = styled(BasicModal)`
  background-color: ${theme.colors.highlight[200]};
  text-align: center;
  margin-top: auto;
  padding-top: 72px;
  width: 100%;

  ${legacyTheme.breakpoints.up('sm')} {
    width: auto;
    min-width: 400px;
    margin-bottom: -24px;
  }

  ${legacyTheme.breakpoints.up('md')} {
    border-top-left-radius: 20px;
    border-top-right-radius: 20px;
  }
`;

const PerkWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 4px 0;
  gap: 12px;
`;

const StyledImg = styled(Image)`
  margin-top: -2px;
`;

type MembershipModalProps = {
  isModalOpen: boolean;
  category: typeof productsCategories.HAIRCARE | typeof productsCategories.SKINCARE;
  closeModal: () => void;
  triggerTrackingEvent?: boolean;
};

export const MembershipModal = ({
  category,
  isModalOpen,
  closeModal,
  triggerTrackingEvent = true,
}: MembershipModalProps) => {
  const { isMobile } = useResponsiveVariant();
  const subscriptionDiscount = useAppSelector(getUserSubscriptionDiscount);
  const membershipPerksContentTitle = useAppSelector(getMembershipPerksTitle);
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (isModalOpen && triggerTrackingEvent) {
      dispatch(trackMembershipModalViewed());
    }
  }, [isModalOpen, triggerTrackingEvent]);

  return (
    <StyledModal
      dataFrom="membership-modal"
      isFullWidth={isMobile}
      isOpen={isModalOpen}
      onClose={closeModal}
    >
      <Box display="flex" flexDirection="column" gap="1x">
        <Text color="neutral.900" id={isModalOpen ? 'modal-title' : ''} variant="body3regular">
          <PerksContentTitle
            discount={subscriptionDiscount}
            title={[...subscriptionPerksContent.title[category][membershipPerksContentTitle]]}
            variant={membershipPerksContentTitle}
          />
        </Text>

        <Text variant="body3regular">+</Text>

        <div id={isModalOpen ? 'modal-description' : ''}>
          {subscriptionPerksContent.content.map(({ logo, text, alt, width, height }) => (
            <PerkWrapper key={text}>
              <StyledImg alt={alt} height={height} src={logo} width={width} />
              <Text color="neutral.900" variant="body4regular">
                {text}
              </Text>
            </PerkWrapper>
          ))}
        </div>
      </Box>
    </StyledModal>
  );
};
